<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import { useGoalsProgress } from 'dashboard/composables/useGoalsProgress';
import ExportPdfButton from '../components/ExportPdfButton.vue';
import {
  commercialLegend,
  serviceLegend,
  pdfNotes as buildNotes,
} from '../exportLegends';
import MyTasksSection from '../components/MeuEspaco/MyTasksSection.vue';
import MyConversationsSection from '../components/MeuEspaco/MyConversationsSection.vue';
import MyCommercialSection from '../components/MeuEspaco/MyCommercialSection.vue';
import MyServiceSection from '../components/MeuEspaco/MyServiceSection.vue';
import MyEvolutionSection from '../components/MeuEspaco/MyEvolutionSection.vue';
import StalledSection from '../components/Comercial/StalledSection.vue';

const { t } = useI18n();
const pageRef = ref(null);
useGoalsProgress({ mine: true });

const PERIODS = [
  { id: 'today', days: 0 },
  { id: 'last7days', days: 6 },
  { id: 'last30days', days: 29 },
  { id: 'last90days', days: 89 },
  { id: 'custom', days: null },
];

// Objeto estável: um `{}` literal no template mudaria a cada renderização e
// refaria a busca da tabela sempre que o período fosse trocado.
const STALLED_PARAMS = {};

const selectedPeriod = ref('last30days');
const customFrom = ref('');
const customUntil = ref('');

// Só os indicadores (Comercial/Atendimento) dependem de período — tarefas e
// conversas mostram o estado atual, sem filtro de tempo.
const dateRange = computed(() => {
  if (selectedPeriod.value === 'custom') {
    return {
      since: customFrom.value ? startOfDay(new Date(customFrom.value)) : null,
      until: customUntil.value ? endOfDay(new Date(customUntil.value)) : null,
    };
  }

  const period = PERIODS.find(item => item.id === selectedPeriod.value);
  const today = new Date();
  return {
    since: startOfDay(subDays(today, period?.days ?? 29)),
    until: endOfDay(today),
  };
});

const hasCompleteRange = computed(
  () => !!dateRange.value.since && !!dateRange.value.until
);

const formattedRange = computed(() => {
  if (!dateRange.value.since || !dateRange.value.until) return '';
  return `${format(dateRange.value.since, 'dd/MM/yyyy')} – ${format(dateRange.value.until, 'dd/MM/yyyy')}`;
});

const pdfLegend = computed(() => [...commercialLegend(t), ...serviceLegend(t)]);
const pdfNotes = computed(() =>
  buildNotes(t, ['SNAPSHOT', 'MY_DATA', 'CURRENCY'])
);

const periodLabel = computed(() => {
  if (selectedPeriod.value === 'custom') return formattedRange.value;
  const name = t(
    `DASHBOARD.ATENDIMENTO.PERIOD.${selectedPeriod.value.toUpperCase()}`
  );
  return `${name} (${formattedRange.value})`;
});

const indicatorParams = computed(() => {
  if (!hasCompleteRange.value) return null;
  return {
    since: Math.floor(dateRange.value.since.getTime() / 1000),
    until: Math.floor(dateRange.value.until.getTime() / 1000),
  };
});
</script>

<template>
  <div
    ref="pageRef"
    class="flex flex-col w-full h-full gap-4 p-6 overflow-y-auto"
  >
    <div data-pdf-ignore class="flex items-center justify-between gap-2">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('DASHBOARD.MEU_ESPACO.TITLE') }}
      </h1>
      <ExportPdfButton
        :target="pageRef"
        :page-title="t('DASHBOARD.MEU_ESPACO.TITLE')"
        slug="meu-espaco"
        :period-label="periodLabel"
        :filter-lines="[]"
        :notes="pdfNotes"
        :legend="pdfLegend"
        :disabled="!hasCompleteRange"
      />
    </div>

    <div
      data-pdf-ignore
      class="flex flex-col gap-3 p-3 border rounded-lg lg:flex-row lg:items-center border-n-weak"
    >
      <div class="flex flex-wrap gap-1">
        <button
          v-for="period in PERIODS"
          :key="period.id"
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="
            selectedPeriod === period.id
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="selectedPeriod = period.id"
        >
          {{ t(`DASHBOARD.ATENDIMENTO.PERIOD.${period.id.toUpperCase()}`) }}
        </button>
      </div>

      <div v-if="selectedPeriod === 'custom'" class="flex items-center gap-2">
        <input
          v-model="customFrom"
          type="date"
          class="!h-8 !py-0 !mb-0 !text-sm rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
        <span class="text-n-slate-10">{{
          t('DASHBOARD.ATENDIMENTO.PERIOD.CUSTOM_UNTIL')
        }}</span>
        <input
          v-model="customUntil"
          type="date"
          class="!h-8 !py-0 !mb-0 !text-sm rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
      </div>
    </div>

    <p v-if="!hasCompleteRange" class="text-sm text-n-slate-11">
      {{ t('DASHBOARD.ATENDIMENTO.PERIOD.CUSTOM_RANGE_INCOMPLETE') }}
    </p>
    <template v-else>
      <MyEvolutionSection :params="indicatorParams" />
      <MyCommercialSection :params="indicatorParams" />
    </template>

    <!-- Retrato do momento atual, sem período: fica fora do v-else acima. -->
    <StalledSection :params="STALLED_PARAMS" mine />

    <MyTasksSection />
    <MyConversationsSection />
    <MyServiceSection v-if="hasCompleteRange" :params="indicatorParams" />
  </div>
</template>
